import { asArray, asRecord, pickFirstString, createLocalId } from '@/utils/AIGuidance'

const toolPattern = /(tool|plugin|function|search|retriev|knowledge|websearch|browse)/i
const reasoningPattern = /(planning|reason|think|analysis|plan)/i

function normalizeStatus(value, fallback = 'completed') {
  const normalized = (value || '').toString().trim().toLowerCase()
  if (!normalized) return fallback
  if (['start', 'started', 'running', 'processing', 'pending', 'in_progress'].includes(normalized)) return 'active'
  if (['done', 'end', 'ended', 'finish', 'finished', 'completed', 'success', 'succeeded'].includes(normalized)) {
    return 'completed'
  }
  if (['error', 'failed', 'fail', 'cancelled', 'canceled'].includes(normalized)) return 'failed'
  return fallback
}

function resolveKind(type, extra) {
  const hint = `${type} ${pickFirstString(extra, ['tool_name', 'tool', 'name'])}`.trim()
  if (toolPattern.test(hint)) return 'tool'
  if (reasoningPattern.test(hint)) return 'reasoning'
  return 'reasoning'
}

function resolveTitle(kind, type, extra) {
  if (kind === 'tool') {
    return (
      pickFirstString(extra, ['title', 'tool_name', 'tool', 'name', 'step_title']) ||
      (toolPattern.test(type) && /search|retriev|websearch|browse/i.test(type) ? '检索资料' : '工具调用')
    )
  }
  return pickFirstString(extra, ['title', 'step_title', 'name']) || '分析中'
}

function resolveContent(record, extra) {
  return (
    pickFirstString(record, ['event_message', 'message', 'text', 'content', 'summary']) ||
    pickFirstString(extra, ['message', 'text', 'content', 'summary', 'observation', 'result', 'output'])
  )
}

function pickTraceSummaryFromRecord(record) {
  const direct = pickFirstString(record, ['trace_summary', 'reasoning_summary', 'thinking_summary'])
  if (direct) return direct

  const nestedKeys = ['trace', 'reasoning', 'thinking', 'agent_trace', 'metadata', 'extra']
  for (const key of nestedKeys) {
    const nested = asRecord(record[key])
    const value = pickFirstString(nested, ['trace_summary', 'reasoning_summary', 'thinking_summary'])
    if (value) return value
  }

  return ''
}

export function adaptTraceSummary(raw) {
  const record = asRecord(raw)
  return pickTraceSummaryFromRecord(record)
}

export function adaptTraceStepsFromAgentEvents(agentEvents) {
  return asArray(agentEvents)
    .map((event, index) => {
      const record = asRecord(event)
      const extra = asRecord(record.event_extra ?? record.extra)
      const type = pickFirstString(record, ['event_type', 'type', 'event_name', 'name']) || pickFirstString(extra, ['type', 'event_type'])
      const kind = resolveKind(type, extra)
      const title = resolveTitle(kind, type, extra)
      const content = resolveContent(record, extra)
      const statusValue = pickFirstString(extra, ['status', 'state']) || pickFirstString(record, ['status', 'state'])
      const status = normalizeStatus(statusValue, 'completed')

      if (!title && !content) return null

      return {
        id: pickFirstString(record, ['event_id', 'id']) || createLocalId(`trace-${index}`),
        kind,
        title,
        content,
        status,
      }
    })
    .filter(Boolean)
}

export function adaptSseEventToTracePatch(event) {
  const extra = asRecord(event.extra)
  const type = typeof event.type === 'string' ? event.type : ''
  const kind = resolveKind(type, extra)

  if (kind === 'reasoning' && !reasoningPattern.test(type) && !pickFirstString(extra, ['status', 'state'])) {
    return null
  }

  const content = resolveContent({ message: event.message }, extra)
  const statusValue = pickFirstString(extra, ['status', 'state'])
  const status = normalizeStatus(statusValue, kind === 'tool' ? 'active' : 'active')

  let phase = 'update'
  if (status === 'failed') {
    phase = 'error'
  } else if (['start', 'started', 'running', 'processing', 'pending', 'in_progress'].includes(statusValue.trim().toLowerCase())) {
    phase = 'start'
  } else if (['done', 'end', 'ended', 'finish', 'finished', 'completed', 'success', 'succeeded'].includes(statusValue.trim().toLowerCase())) {
    phase = 'end'
  } else if (!statusValue && !content) {
    phase = 'start'
  }

  return {
    kind,
    phase,
    title: resolveTitle(kind, type, extra),
    content,
    status,
  }
}
